<template>
  <div>
    <flow-layout :current-step-index="1">
      <heading level="2">{{ $t('brokerDetails.title') }}</heading>

      <form autocomplete="off">
        <input-inline>
          <input-text
            id="partner-code"
            v-model="application._extension.partnerCode"
            name="partner-code"
            @input="checkFormValidation"
            v-validation="[['dynamic', '/_extension/partnerCode']]"
            :maxlength="PARTNER_CODE_MAX_LENGTH"
          >
            <template #label>{{ $t('brokerDetails.partnerCodeLabel') }}</template>
          </input-text>
        </input-inline>

        <input-inline>
          <input-text
            id="broker-full-name"
            v-model="application._extension.brokerFullName"
            name="broker-full-name"
            @input="checkFormValidation"
            :maxlength="BROKER_FULL_NAME_MAX_LENGTH"
            :strict-mode="{ regex: /\./g }"
            v-validation="[['dynamic', '/_extension/brokerFullName']]"
          >
            <template #label>{{ $t('brokerDetails.brokerFullName') }}</template>
          </input-text>
        </input-inline>

        <input-inline>
          <input-text
            id="broker-email"
            @input="checkFormValidation"
            v-model="application._extension.brokerEmail"
            name="broker-email"
            :maxlength="EMAIL_MAX_LENGTH"
            v-validation="[['dynamic', '/_extension/brokerEmail']]"
            :strictMode="{ skipDefault: true, regex: 'email' }"
          >
            <template #label>{{ $t('brokerDetails.brokerEmail') }}</template>
          </input-text>
        </input-inline>

        <input-radio-group
          id="pdf-consent"
          class="haveCreditConsent"
          @change="onCreditConsentChange"
          v-model="primaryOwner._extension.haveCreditConsent"
          :options="haveCreditConsentOptions"
          v-validation.noEvent="[['dynamic', '/owners/*/_extension/haveCreditConsent']]"
        >
          <template #legend>{{ $t('brokerDetails.haveCreditConsent.label') }}</template>
        </input-radio-group>

        <div
          v-if="primaryOwner._extension.haveCreditConsent === true && creditConsentAdr && !loadingAdrs"
          class="pdfUploadSection"
        >
          <div class="uploadedFiles" v-if="uploadedFiles.length">
            <uploaded-file
              v-for="(file, index) in uploadedFiles"
              :file="file"
              :key="index"
              class="file"
              @delete="deleteFile"
            />
          </div>

          <bank-statement-drop-zone
            :adr="creditConsentAdr"
            @picked="uploadFile"
            @delete="deleteFile"
            class="pdfDropZone"
          />

          <p v-if="pdfFileError" class="pdfFileError">{{ pdfFileError }}</p>
        </div>

        <p
          v-if="primaryOwner._extension.haveCreditConsent === true && adrCreationError && !creditConsentAdr"
          class="pdfFileError"
        >
          {{ $t('brokerDetails.adrCreationError') }}
        </p>

        <input-checkbox
          id="dont-send-auto-email"
          class="dontSendAutoEmail"
          :checked="application._extension.dontSendAutoEmail"
          @change="onDontSendAutoEmailChange"
        >
          <template #label>{{ $t('brokerDetails.dontSendAutoEmail') }}</template>
        </input-checkbox>

        <consents
          class="applicationConsents"
          :pageConsents="applicationConsents"
          :consentsPath="consentsPath"
          v-model="application.consents"
          @input="checkFormValidation"
        />

        <navigation-buttons
          :back="false"
          @next="onSubmit"
          :next="{
            label: $t('nextButtonLabel'),
            modifiers: { disabled: disableNext },
          }"
        />
      </form>
    </flow-layout>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import FlowLayout from '@/views/layouts/FlowLayout';
import InputInline from '@/components/InputInline';
import BankStatementDropZone from '@/components/BankStatementDropZone';
import UploadedFile from '@/components/UploadedFile';
import Consents from '@/components/Consents';
import applicationConsents, {
  MINIMUM_TRADING_TIME_AND_TURNOVER_CONSENT,
} from '@/modules/application-consents';
import mapConstsToOptions from '@/utils/map-consts-to-options';
import applicationMixin from '@/mixins/application';
import uploadFilesMixin from '@/mixins/upload-files';
import {
  EMAIL_MAX_LENGTH,
  PARTNER_CODE_MAX_LENGTH,
  BROKER_FULL_NAME_MAX_LENGTH,
} from '@/modules/validation-service/fields-length';
import { createAdrs } from '@/modules/api-service/v3';

export default {
  name: 'BrokerDetails',
  components: {
    FlowLayout,
    InputInline,
    BankStatementDropZone,
    UploadedFile,
    Consents,
  },
  data() {
    return {
      EMAIL_MAX_LENGTH,
      PARTNER_CODE_MAX_LENGTH,
      BROKER_FULL_NAME_MAX_LENGTH,
      isFormValid: false,
      pdfFileError: '',
      loadingAdrs: false,
      adrCreationError: false,
    };
  },
  computed: {
    ...mapGetters({
      filterFilesByQuery: 'file/filterFilesByQuery',
    }),
    applicationConsents() {
      return applicationConsents.filter(
        consent => consent.type !== MINIMUM_TRADING_TIME_AND_TURNOVER_CONSENT.type,
      );
    },
    primaryOwner: {
      get() {
        return this.application.owners ? this.application.owners[0] : {};
      },
      set(value) {
        this.application = { ...this.application, owners: [value] };
      },
    },
    creditConsentAdr() {
      const adrs = this.$store.getters['adrs/getAdrs'];
      return adrs.find(adr => adr.template && adr.template.name === 'creditConsent');
    },
    uploadedFiles() {
      if (!this.creditConsentAdr) return [];
      return this.filterFilesByQuery({ adrId: this.creditConsentAdr.id }).filter(
        file => file.isUploaded,
      );
    },
    haveCreditConsentOptions() {
      return mapConstsToOptions([true, false], 'brokerDetails.haveCreditConsent.options');
    },
    consentsPath() {
      return 'brokerDetails.consents';
    },
    disableNext() {
      if (!this.isFormValid) return true;
      if (this.isUploadInProgress) return true;
      if (this.fileUploadServiceError) return true;
      if (this.primaryOwner._extension?.haveCreditConsent == null) {
        return true;
      }
      if (
        this.primaryOwner._extension?.haveCreditConsent === true &&
        !this.uploadedFiles.length
      ) {
        return true;
      }
      return false;
    },
  },
  methods: {
    ...mapActions('file', ['deleteFile']),
    async ensureCreditConsentAdr() {
      if (this.creditConsentAdr) return;

      this.loadingAdrs = true;
      this.adrCreationError = false;
      try {
        const response = await createAdrs(this.application.id, ['creditConsent']);
        const newAdrs = response.data;
        const existingAdrs = this.$store.getters['adrs/getAdrs'];
        // createAdrs store action replaces state, so merge to preserve ADRs from BasicInfo
        const mergedAdrs = [...existingAdrs];
        newAdrs.forEach(newAdr => {
          const index = mergedAdrs.findIndex(adr => adr.id === newAdr.id);
          if (index >= 0) {
            mergedAdrs[index] = newAdr;
          } else {
            mergedAdrs.push(newAdr);
          }
        });
        await this.$store.dispatch('adrs/createAdrs', mergedAdrs);
      } catch (error) {
        this.adrCreationError = true;
        console.error('[BrokerDetails] Credit consent ADR creation failed:', error);
      } finally {
        this.loadingAdrs = false;
      }
    },
    uploadFile(payload) {
      const { files } = payload;
      const nonPdfFiles = files.filter(file => file.type !== 'application/pdf');
      if (nonPdfFiles.length) {
        this.pdfFileError = this.$t('brokerDetails.pdfUpload.invalidType');
        return;
      }
      this.pdfFileError = '';
      this.$store.dispatch('file/uploadFile', payload);
    },
    checkFormValidation() {
      this.$nextTick().then(() => {
        this.isFormValidFn();
      });
    },
    isFormValidFn() {
      let validationCounter = 0;
      this.isFormValid = false;
      this.$validations.collections[0].forEach((value, key) => {
        if (key === 'pdf-consent' && value.component.errorMessage === '') {
          validationCounter += 1;
        } else if (
          key.includes('agreement-checkbox') &&
          value.component.checked === true &&
          value.component.errorMessage === ''
        ) {
          validationCounter += 1;
        } else if (value.component.empty === false && value.component.errorMessage === '') {
          validationCounter += 1;
        }
      });
      this.isFormValid = validationCounter === this.$validations.collections[0].size;
    },
    onCreditConsentChange() {
      this.pdfFileError = '';
      // Delete any uploaded files for this ADR when switching away from "Yes"
      if (this.primaryOwner._extension.haveCreditConsent === false) {
        this.uploadedFiles.forEach(file => this.deleteFile(file));
      }
      this.checkFormValidation();
    },
    onDontSendAutoEmailChange(value) {
      this.application = {
        ...this.application,
        _extension: { ...this.application._extension, dontSendAutoEmail: value },
      };
    },
    async onSubmit() {
      if (!this.$validations.touch()) {
        this.$validations.scrollToFirstError();
        return;
      }
      const patch = this.generatePatch();
      if (patch.length) {
        const result = await this.patchStage({ stage: 'BrokerDetails', patch });
        await this.$store.dispatch('application/updateApplication', result);
      }
      this.$router.push({ name: this.$router.getRouteByStage('FundingNeeds') });
    },
  },
  mixins: [applicationMixin, uploadFilesMixin],
  async mounted() {
    await this.ensureCreditConsentAdr();
  },
};
</script>

<style lang="sass" scoped>
.dontSendAutoEmail
  margin-top: $glass-spacing-4

.haveCreditConsent
  margin-top: $glass-spacing-4

.pdfUploadSection
  margin-top: $glass-spacing-3

.uploadedFiles
  margin-top: $glass-spacing-3
  .file + .file
    margin-top: $glass-spacing-1

.pdfDropZone
  margin-top: $glass-spacing-3

.pdfFileError
  +font-size(13.33)
  color: $glass-color-system-validation-error-dark-03
  margin-top: $glass-spacing-1

.applicationConsents
  margin-top: $glass-spacing-2
  white-space: pre-line
</style>
